import { SubscriptionPlan, SUBSCRIPTION_PLAN_IDS } from '../types/subscription';

// Subscription Plans Configuration
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: SUBSCRIPTION_PLAN_IDS.FREE,
    name: 'Free Plan',
    nameAr: 'الخطة المجانية',
    description: 'Perfect for solo lawyers getting started',
    descriptionAr: 'مثالية للمحامين الأفراد الذين بدأوا للتو',
    price: 0,
    currency: 'EGP',
    isFreeTier: true,
    
    // Usage Limits (simplified structure)
    maxOffices: 1,
    maxClientsTotal: 3, // Total clients across all offices
    maxFilesTotal: 3,    // Total files across all clients
    
    // Feature Access
    includesEventReminders: false,
    includesAdvancedStats: false,
    includesCommunity: false,
    includesPhoneSupport: false,
    includesExportExcel: false,
    
    // Billing
    billingCycle: 'monthly',
    trialDays: 0,
    
    // UI Properties
    color: 'gray',
    icon: 'gift',
    features: [
      '1 Office',
      '3 Total Clients',
      '3 Total Files',
      'Basic Event Management',
      'File Attachments',
      'Basic Finance Tracking',
      'Lawyer Guide Access',
      'Email Support'
    ],
    featuresAr: [
      'مكتب واحد',
      '3 عملاء إجمالي',
      '3 ملفات إجمالي',
      'إدارة الأحداث الأساسية',
      'مرفقات الملفات',
      'تتبع المالية الأساسي',
      'الوصول إلى دليل المحامي',
      'دعم البريد الإلكتروني'
    ]
  },
  {
    id: SUBSCRIPTION_PLAN_IDS.BASIC,
    name: 'Basic Plan',
    nameAr: 'الخطة الأساسية',
    description: 'Ideal for growing law practices',
    descriptionAr: 'مثالية لمكاتب المحاماة النامية',
    price: 99,
    currency: 'EGP',
    isFreeTier: false,
    
    // Usage Limits (simplified structure)
    maxOffices: 2,
    maxClientsTotal: 10, // Total clients across all offices
    maxFilesTotal: 10,   // Total files across all clients
    
    // Feature Access
    includesEventReminders: true,
    includesAdvancedStats: false,
    includesCommunity: false,
    includesPhoneSupport: false,
    includesExportExcel: false,
    
    // Billing
    billingCycle: 'monthly',
    trialDays: 14,
    
    // UI Properties
    color: 'blue',
    icon: 'star',
    popular: true,
    features: [
      '2 Offices',
      '10 Total Clients',
      '10 Total Files',
      'Event Reminders',
      'All Free Features',
      'Priority Email Support',
      'Basic Analytics'
    ],
    featuresAr: [
      'مكتبان',
      '10 عملاء إجمالي',
      '10 ملفات إجمالي',
      'تذكيرات الأحداث',
      'جميع الميزات المجانية',
      'دعم البريد الإلكتروني ذو الأولوية',
      'التحليلات الأساسية'
    ]
  },
  {
    id: SUBSCRIPTION_PLAN_IDS.PROFESSIONAL,
    name: 'Professional Plan',
    nameAr: 'الخطة المهنية',
    description: 'Complete solution for established law firms',
    descriptionAr: 'حل شامل لمكاتب المحاماة الراسخة',
    price: 149,
    currency: 'EGP',
    isFreeTier: false,
    
    // Usage Limits (simplified structure)
    maxOffices: -1,        // Unlimited (displayed as -1)
    maxClientsTotal: -1,   // Unlimited (displayed as -1)
    maxFilesTotal: -1,     // Unlimited (displayed as -1)
    maxTeamMembers: -1,    // Unlimited (displayed as -1)
    
    // Feature Access
    includesEventReminders: true,
    includesAdvancedStats: true,
    includesCommunity: true,
    includesPhoneSupport: true,
    includesExportExcel: true,
    
    // Team Management
    teamMemberPrice: 49,
    
    // Billing
    billingCycle: 'monthly',
    trialDays: 14,
    
    // UI Properties
    color: 'purple',
    icon: 'crown',
    features: [
      'Unlimited Offices',
      'Unlimited Clients',
      'Unlimited Files',
      'Team Management',
      'Advanced Statistics',
      'Community Access',
      'Phone Support',
      'Export Excel',
      'All Basic Features',
      'Priority Support',
      'Custom Integrations'
    ],
    featuresAr: [
      'مكاتب غير محدودة',
      'عملاء غير محدودين',
      'ملفات غير محدودة',
      'إدارة الفريق',
      'إحصائيات متقدمة',
      'الوصول إلى المجتمع',
      'دعم الهاتف',
      'تصدير إكسل',
      'جميع الميزات الأساسية',
      'دعم ذو أولوية',
      'تكاملات مخصصة'
    ]
  }
];

// Helper functions
export const getPlanById = (planId: string): SubscriptionPlan | undefined => {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
};

export const getFreePlan = (): SubscriptionPlan => {
  return SUBSCRIPTION_PLANS.find(plan => plan.isFreeTier) || SUBSCRIPTION_PLANS[0];
};

export const getBasicPlan = (): SubscriptionPlan => {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === SUBSCRIPTION_PLAN_IDS.BASIC) || SUBSCRIPTION_PLANS[1];
};

export const getProfessionalPlan = (): SubscriptionPlan => {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL) || SUBSCRIPTION_PLANS[2];
};

export const getPaidPlans = (): SubscriptionPlan[] => {
  return SUBSCRIPTION_PLANS.filter(plan => !plan.isFreeTier);
};

export const getPopularPlan = (): SubscriptionPlan | undefined => {
  return SUBSCRIPTION_PLANS.find(plan => plan.popular);
};

// Plan comparison helpers
export const comparePlans = (plan1Id: string, plan2Id: string) => {
  const plan1 = getPlanById(plan1Id);
  const plan2 = getPlanById(plan2Id);
  
  if (!plan1 || !plan2) return null;
  
  return {
    priceDifference: plan2.price - plan1.price,
    featureDifference: plan2.features.length - plan1.features.length,
    isUpgrade: plan2.price > plan1.price,
    isDowngrade: plan2.price < plan1.price
  };
};

// Feature access helpers
export const hasFeatureAccess = (planId: string, feature: keyof Pick<SubscriptionPlan, 'includesEventReminders' | 'includesAdvancedStats' | 'includesCommunity' | 'includesPhoneSupport'>): boolean => {
  const plan = getPlanById(planId);
  return plan ? plan[feature] : false;
};

export const canCreateOffice = (planId: string, currentOffices: number): boolean => {
  const plan = getPlanById(planId);
  if (!plan) return false;
  return plan.maxOffices === -1 || currentOffices < plan.maxOffices;
};

export const canCreateClient = (planId: string, currentClients: number): boolean => {
  const plan = getPlanById(planId);
  if (!plan) return false;
  return plan.maxClientsTotal === -1 || currentClients < plan.maxClientsTotal;
};

export const canCreateFile = (planId: string, currentFiles: number): boolean => {
  const plan = getPlanById(planId);
  if (!plan) return false;
  return plan.maxFilesTotal === -1 || currentFiles < plan.maxFilesTotal;
};

// Plan upgrade suggestions
export const getSuggestedPlan = (currentPlanId: string, usage: { offices: number; clients: number; files: number }): SubscriptionPlan | null => {
  const currentPlan = getPlanById(currentPlanId);
  if (!currentPlan) return null;
  
  // Check if user needs more offices
  if (currentPlan.maxOffices !== -1 && usage.offices >= currentPlan.maxOffices) {
    return getProfessionalPlan();
  }
  
  // Check if user needs more clients
  if (currentPlan.maxClientsTotal !== -1 && usage.clients >= currentPlan.maxClientsTotal) {
    if (currentPlan.id === SUBSCRIPTION_PLAN_IDS.FREE) {
      return getBasicPlan();
    } else {
      return getProfessionalPlan();
    }
  }
  
  // Check if user needs more files
  if (currentPlan.maxFilesTotal !== -1 && usage.files >= currentPlan.maxFilesTotal) {
    if (currentPlan.id === SUBSCRIPTION_PLAN_IDS.FREE) {
      return getBasicPlan();
    } else {
      return getProfessionalPlan();
    }
  }
  
  return null;
};
